/**
 * Form Template Manager Component
 * 
 * Provides functionality to:
 * - Save form submissions as templates
 * - List available templates
 * - Use templates to pre-populate forms
 * - Manage template permissions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Form, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import { FaSave, FaList, FaClone, FaTrash, FaEye } from 'react-icons/fa';
import toast from 'react-hot-toast';
import moment from 'moment';

// Services
import { formTemplateService } from '../../services/formTemplateService';

const FormTemplateManager = ({ formId, currentFormData, onTemplateApply }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Template creation state
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await formTemplateService.getFormTemplates(formId);
      if (response.success) {
        // Handle both array response and object response with data property
        const templatesData = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || []);
        setTemplates(templatesData);
      } else {
        toast.error('Failed to load templates');
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error loading templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    if (formId) {
      fetchTemplates();
    }
  }, [formId, fetchTemplates]);

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    if (!currentFormData || Object.keys(currentFormData).length === 0) {
      toast.error('No form data to save as template');
      return;
    }

    try {
      setSaving(true);
      const templateData = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        form_id: formId,
        form_data: currentFormData,
        is_public: isPublic,
      };

      const response = await formTemplateService.createTemplate(formId, templateData);
      if (response.success) {
        toast.success('Template saved successfully');
        setShowSaveModal(false);
        setTemplateName('');
        setTemplateDescription('');
        setIsPublic(false);
        await fetchTemplates();
      } else {
        toast.error(response.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error saving template');
    } finally {
      setSaving(false);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      if (onTemplateApply) {
        onTemplateApply(template.form_data);
        toast.success(`Template "${template.name}" applied successfully`);
        setShowTemplateModal(false);
      }
    } catch (error) {
      console.error('Error applying template:', error);
      toast.error('Error applying template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const response = await formTemplateService.deleteTemplate(templateId);
      if (response.success) {
        toast.success('Template deleted successfully');
        await fetchTemplates();
      } else {
        toast.error(response.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error deleting template');
    }
  };

  const handleViewTemplate = (template) => {
    // You could open a modal to show template details
    console.log('View template:', template);
  };

  const getTemplateIcon = (template) => {
    return template.is_public ? (
      <Badge bg="success" className="me-2">Public</Badge>
    ) : (
      <Badge bg="secondary" className="me-2">Private</Badge>
    );
  };

  return (
    <Card className="mt-3">
      {/* <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <FaList className="me-2" />
            Form Templates
          </div>
          <div>
            <Button 
              variant="outline-primary" 
              size="sm" 
              className="me-2"
              onClick={() => setShowTemplateModal(true)}
              disabled={!templates || templates.length === 0}
            >
              <FaEye className="me-1" />
              Use Template
            </Button>
            <Button 
              variant="outline-success" 
              size="sm"
              onClick={() => setShowSaveModal(true)}
              disabled={!currentFormData || Object.keys(currentFormData).length === 0}
            >
              <FaSave className="me-1" />
              Save as Template
            </Button>
          </div>
        </div>
      </Card.Header> */}
      {/* <Card.Body>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading templates...
          </div>
        ) : templates.length === 0 ? (
          <Alert variant="info" className="mb-0">
            No templates available for this form. Create one by filling out the form and saving it as a template.
          </Alert>
        ) : (
          <div className="template-list">
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Visibility</th>
                  <th>Created</th>
                  <th>Usage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(templates) && templates.map((template) => (
                  <tr key={template.template_id}>
                    <td>
                      <strong>{template.name}</strong>
                    </td>
                    <td>
                      <small className="text-muted">
                        {template.description || 'No description'}
                      </small>
                    </td>
                    <td>{getTemplateIcon(template)}</td>
                    <td>
                      <small>{moment(template.created_at).format('DD/MM/YYYY')}</small>
                    </td>
                    <td>
                      <Badge bg="info">{template.usage_count || 0} uses</Badge>
                    </td>
                    <td>
                      <div className="btn-group" role="group">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleUseTemplate(template)}
                          title="Use this template"
                        >
                          <FaClone />
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleViewTemplate(template)}
                          title="View template details"
                        >
                          <FaEye />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteTemplate(template.template_id)}
                          title="Delete template"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body> */}

      {/* Save Template Modal */}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Save Form as Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            This will save the current form data as a reusable template that can be applied to future form submissions.
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Template Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Enter a descriptive name for this template"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="Optional description of when to use this template"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="isPublic"
              label="Make this template available to all users"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Public templates can be used by any user with access to this form
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSaveTemplate} disabled={saving || !templateName.trim()}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save Template
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Use Template Modal */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Select Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Warning:</strong> Using a template will replace all current form data. Make sure to save your current work if needed.
          </Alert>

          {!Array.isArray(templates) || templates.length === 0 ? (
            <Alert variant="info">No templates available for this form.</Alert>
          ) : (
            <div className="template-selection">
              {templates.map((template) => (
                <Card key={template.template_id} className="mb-3">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6>
                          {getTemplateIcon(template)}
                          {template.name}
                        </h6>
                        <p className="text-muted mb-2">
                          {template.description || 'No description available'}
                        </p>
                        <small className="text-muted">
                          Created: {moment(template.created_at).format('DD/MM/YYYY')} •
                          Used: {template.usage_count || 0} times
                        </small>
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => handleUseTemplate(template)}
                      >
                        Use Template
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default FormTemplateManager;